import { timeUnitOf, truncSql, timeKeySql, intervalSql, bucketStartSql } from '@rdrive/plugin-chart-time-series';
import { queryRows } from '@rdrive/data-duckdb';
import { makeBandTitles } from './issueStatusBands';

// Status burn-up ("how many issues sit in each status colour over time") for
// @rdrive/plugin-chart-time-series' BaseTimeSeriesChart (mode="cumulative", bandOrder="asc") —
// IA_ABSORPTION_PLAN Phase 4 step 12c; was components/charts/IssueStatusHistoryBurndownLineChart.vue,
// whose query strings these are, VERBATIM (the bucket expressions now come from the package's
// `timeBuckets` helpers, which print the same text — except the week key's year, see ISO_YEAR; the
// tooltip's week boundary is `bucketStartSql`'s — the same ISO Jan-4 expression without the old
// inline SQL comment). The
// series objects, colours, tooltip markup, zoom and PNG export are the base's.
//
// The page supplies the filter contexts (this config has none of its own, so it uses plain
// `queryRows` rather than `makeChartConfig`, like project-overview's time-series configs). The
// SQL reads `filteredStatusHistory` (the status-history context's view) joined to
// `filter_context` (the issues context's table) — see the page for which ticks reload it.
//
// Returns { fetchSeries, fetchTooltip, fetchLegendTitles, resolveGroupBy }, spread onto the base.
// Week keys carry the ISO year (`%G-W%V`, not the old `%Y-W%V`): a late-December Monday that starts
// ISO week 1 of the next year no longer shares the key of the previous January's week 1 — the old
// SFC folded that week into January's slot (its values overwrote January's) and labelled the last
// week of 2025 "Week 01, 2025". See `timeKeySql` in @rdrive/plugin-chart-time-series.
const ISO_YEAR = { isoYear: true };

export function useIssueBurndownChart({ conn }) {
  const run = (query) => queryRows(conn, query);
  // The PNG legend's names: the status titles each colour band carried in the last series load
  const bandTitles = makeBandTitles();

  const generateBurnupQuery = (groupBy = "day") => {
    /* --- literals that vary with the caller ------------------------ */
    const unit = timeUnitOf(groupBy);
    const interval = intervalSql(unit);

    /* --- full SQL --------------------------------------------------- */
    return `
WITH
/* 1️⃣  +1 / −1 "delta" rows for each status period ------------------ */
deltas AS (
  SELECT sht.subItemId,
         sht.statusId,
         ${truncSql(unit, 'sht.validFrom')}                                  AS bucket,
         +1 AS delta
    FROM filteredStatusHistory sht
    JOIN filter_context fc ON sht.subItemId = fc.Id
  UNION ALL
  SELECT sht.subItemId,
         sht.statusId,
         DATE_TRUNC('${unit}',
                    COALESCE(sht.validTo, CAST('9999-12-31' AS TIMESTAMP)))     AS bucket,
         -1 AS delta
    FROM filteredStatusHistory sht
    JOIN filter_context fc ON sht.subItemId = fc.Id
),

/* 2️⃣  aggregate deltas per bucket × colour and title --------------- */
bucket_deltas AS (
  SELECT bucket,
         st.color,
         st.title,
         st.sort,
         SUM(delta) AS delta
    FROM deltas d
         JOIN status_templates st ON d.statusId = st.id

   GROUP BY bucket, st.color, st.title, st.sort
),

/* 3️⃣  canonical sort order per colour ------------------------------ */
color_sort_map AS (
  SELECT color, MIN(sort) AS standard_sort
    FROM status_templates
   GROUP BY color
),

/* 4️⃣  calendar buckets from first item to "now" -------------------- */
all_buckets AS (
  SELECT ${truncSql(unit, 'gs')} AS bucket
    FROM GENERATE_SERIES(
           (SELECT ${truncSql(unit, 'MIN(createdDate)')} FROM status_history),
           (SELECT ${truncSql(unit, 'CAST(CURRENT_TIMESTAMP AS TIMESTAMP)')}),
           ${interval}
         ) AS series(gs)
),

/* 5️⃣  cross product: every bucket × every colour ------------------- */
all_buckets_and_colors AS (
  SELECT b.bucket,
         c.color,
         c.standard_sort
    FROM all_buckets b
    CROSS JOIN color_sort_map c
),

/* 6️⃣  add real deltas and accumulate running sum ------------------ */
running AS (
  SELECT  abc.bucket,
          abc.color,
          abc.standard_sort,
          COALESCE(bd.delta, 0) AS delta,
          COALESCE(STRING_AGG(DISTINCT bd.title, ', '), '') AS titles,
          SUM(COALESCE(bd.delta, 0))
              OVER (PARTITION BY abc.color ORDER BY abc.bucket)           AS cumulative_count
    FROM  all_buckets_and_colors abc
          LEFT JOIN bucket_deltas bd
                 ON  abc.bucket = bd.bucket
                 AND abc.color  = bd.color
    GROUP BY abc.bucket, abc.color, abc.standard_sort, bd.delta
)

/* 7️⃣  final projection for the chart ------------------------------ */
SELECT
    ${timeKeySql(unit, 'bucket', ISO_YEAR)} AS time_key,
    color as color,
    standard_sort AS sort,
    titles as titles,
    cumulative_count AS count
  FROM running
 ORDER BY bucket, sort;
`;
  };

  // Query for detailed tooltip data: the issues alive in the bucket, by status. `bucketStartSql`
  // THROWS on a malformed key; the base catches per fetch and renders an empty breakdown.
  const generateTooltipQuery = (timePoint, groupBy = 'day') => {
    /* ---------- derive unit + “bucket start” timestamp ------------- */
    const unit = timeUnitOf(groupBy);
    const boundaryExpr = bucketStartSql(unit, timePoint);

    /* ---------- SQL ------------------------------------------------ */
    return `
WITH
  /* bucket start as TIMESTAMP + its DATE_TRUNC(unit, …) value */
  boundary AS (
    SELECT
      ${boundaryExpr}                           AS ts,
      ${truncSql(unit, boundaryExpr)}    AS bucket
  ),

  /* items that are alive in this bucket --------------------------- */
  status_at_boundary AS (
    SELECT
      sht.subItemId,
      sht.statusId
    FROM   boundary b
    JOIN   filteredStatusHistory sht
           ON  ${truncSql(unit, 'sht.validFrom')} <= b.bucket
           AND (
                 sht.validTo IS NULL
                 OR ${truncSql(unit, 'sht.validTo')} > b.bucket
               )
    JOIN   filter_context fc ON sht.subItemId = fc.Id
  ),

  /* aggregated counts by status title / colour -------------------- */
  counts AS (
    SELECT
      st.title,
      st.color,
      MIN(st.sort)              AS sort,
      COUNT(DISTINCT subItemId) AS count
    FROM   status_at_boundary sab
    JOIN   status_templates st  ON sab.statusId = st.id
    GROUP  BY st.title, st.color
  )

/* final projection ------------------------------------------------ */
SELECT
  title as title,
  color as color,
  sort as sort,
  count as count
FROM counts
ORDER BY sort, title;
`;
  };

  // Rows: { timeKey, color (may be null), value, sort } — `value` is the running total the SQL
  // above accumulates (aliased `count`); each row's `titles` feed the PNG legend's band names.
  const fetchSeries = async (groupBy) => {
    const rows = await run(generateBurnupQuery(groupBy));
    bandTitles.collect(rows);
    return rows.map((row) => ({
      timeKey: row.time_key,
      color: row.color,
      value: Number(row.count),
      sort: Number(row.sort)
    }));
  };

  // Rows: { title, color, sort, count } for one time bucket
  const fetchTooltip = async (timePoint, groupBy) => {
    const rows = await run(generateTooltipQuery(timePoint, groupBy));
    return rows.map((row) => ({
      ...row,
      count: Number(row.count),
      sort: Number(row.sort)
    }));
  };

  // The bucket to open on, from the span of ALL status history (not the filtered view): up to two
  // weeks → day, up to ~three months → week, else month. The base awaits it before its first load.
  const resolveGroupBy = async () => {
    try {
      const rows = await run(`
      SELECT
        MIN(createdDate) as min_date,
        MAX(createdDate) as max_date
      FROM status_history
    `);
      const row = rows[0];
      if (!row || !row.min_date || !row.max_date) return 'month';

      const minDate = new Date(row.min_date);
      const maxDate = new Date(row.max_date);
      const diffDays = (maxDate - minDate) / (1000 * 60 * 60 * 24);

      if (diffDays <= 14) return 'day';
      if (diffDays <= 90) return 'week';
      return 'month';
    } catch (e) {
      console.error('Error determining optimal groupBy:', e);
      return 'month';
    }
  };

  return { fetchSeries, fetchTooltip, fetchLegendTitles: bandTitles.fetchLegendTitles, resolveGroupBy };
}
